// # Mentor Calendar Component
import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FaVideo, FaClock, FaCalendarAlt, FaUser, FaInfoCircle, FaUsers, FaTimes, FaClipboardCheck } from 'react-icons/fa';
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { db, auth } from '../../../../auth/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthStore } from '../../../../auth/state';
import { useNavigate } from 'react-router-dom';
import cacheManager from '../../../../auth/cache_manager';
// # Import FullCalendar styles
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import './mentor_calendar.css';

const MentorCalendar = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  // # State for managing events and selected event
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hostData, setHostData] = useState({});
  const [participantsData, setParticipantsData] = useState({});
  const [showParticipants, setShowParticipants] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Get meeting status based on time
  const getMeetingStatus = (meetTime) => {
    const now = new Date();
    const meetingTime = new Date(meetTime);
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
    const hoursAfterMeeting = (now - meetingTime) / (1000 * 60 * 60);
    
    if (hoursUntilMeeting < 0 && hoursAfterMeeting <= 6) {
      // # Meeting has passed but within 6 hours window - still joinable
      return 'joinable';
    } else if (hoursUntilMeeting < 0) {
      return 'past';
    } else if (hoursUntilMeeting <= 1) {
      return 'joinable';
    } else if (hoursUntilMeeting <= 24) {
      return 'upcoming-soon';
    } else if (hoursUntilMeeting <= 168) { // # 7 days = 168 hours
      return 'upcoming-week';
    } else {
      return 'scheduled';
    }
  };

  // # Get checkup status based on time and completion
  const getCheckupStatus = (checkupTime, completed) => {
    if (completed) return 'completed';
    
    const now = new Date();
    const checkupDate = new Date(checkupTime);
    const hoursUntilCheckup = (checkupDate - now) / (1000 * 60 * 60);
    
    if (hoursUntilCheckup < 0) {
      return 'overdue';
    } else if (hoursUntilCheckup <= 24) {
      return 'due-soon';
    } else {
      return 'scheduled';
    }
  };

  // # Fetch user data for hosts and participants
  const fetchUserData = async (meetings) => {
    // # Collect unique user IDs for both hosts and participants
    const uniqueHostIds = [...new Set(meetings.map(meeting => meeting.host.uid))];
    const uniqueParticipantIds = [...new Set(meetings.flatMap(meeting => meeting.invited || []))];
    
    // # Fetch host data
    const hostDataPromises = uniqueHostIds.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return [uid, {
            displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'Unknown',
            email: data.email || 'No email',
            avatar: data.profileImage || data.photoURL || null,
            username: data.username || null
          }];
        }
      } catch (error) {
        console.error(`Error fetching user data for ${uid}:`, error);
      }
      return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
    });

    // # Fetch participant data
    const participantDataPromises = uniqueParticipantIds.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return [uid, {
            displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'Unknown',
            email: data.email || 'No email',
            avatar: data.profileImage || data.photoURL || null,
            username: data.username || null
          }];
        }
      } catch (error) {
        console.error(`Error fetching participant data for ${uid}:`, error);
      }
      return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
    });

    const hostDataResults = await Promise.all(hostDataPromises);
    const participantDataResults = await Promise.all(participantDataPromises);
    
    setHostData(Object.fromEntries(hostDataResults));
    setParticipantsData(Object.fromEntries(participantDataResults));
  };

  // # Fetch meetings and checkups on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      if (!user?.uid) return;

      try {
        // # Fetch meetings
        const meetingTypes = ['gen_meetings', 'step1_meetings', 'step2_meetings', 'oet_meetings', 'visa_meetings'];
        let allMeetings = [];

        for (const type of meetingTypes) {
          const meetingsRef = doc(db, 'meetings', type);
          const meetingsDoc = await getDoc(meetingsRef);

          if (meetingsDoc.exists()) {
            const data = meetingsDoc.data();
            const typeMeetings = Object.entries(data).map(([id, meeting]) => ({
              id,
              type,
              ...meeting,
              meet_time: meeting.meet_time?.toDate?.() || new Date(meeting.meet_time)
            })).filter(meeting => 
              meeting.host.uid === user.uid || 
              meeting.invited.includes(user.uid)
            );
            allMeetings = [...allMeetings, ...typeMeetings];
          }
        }

        // # Sort meetings and fetch host data
        allMeetings.sort((a, b) => a.meet_time - b.meet_time);
        await fetchUserData(allMeetings);

        // # Convert meetings to calendar events
        const meetingEvents = allMeetings.map(meeting => {
          const status = getMeetingStatus(meeting.meet_time);
          return {
            id: meeting.id,
            title: meeting.title,
            start: meeting.meet_time,
            end: new Date(new Date(meeting.meet_time).getTime() + 3600000), // # 1 hour duration
            className: `mntr_cldr_event-${status}`,
            extendedProps: {
              type: meeting.type,
              description: meeting.description,
              hostId: meeting.host.uid,
              isHost: meeting.host.uid === user.uid,
              meetingUrl: meeting.host.url,
              status: status,
              invited: meeting.invited || [],
              eventType: 'meeting'
            }
          };
        });

        // # Fetch checkups - first check if user is admin/mentor or tutor
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userRole = userDoc.exists() ? userDoc.data().role : null;
        
        const mentorDocRef = doc(db, 'mentors', user.uid);
        const mentorDoc = await getDoc(mentorDocRef);
        let checkupEvents = [];

        if (mentorDoc.exists() || userRole === 'tutor') {
          // # For tutors who might not have a mentor doc, we'll still show calendar
          // # but without checkups until they set up their mentor profile
          if (mentorDoc.exists()) {
            const mentorData = mentorDoc.data();
            const menteeCheckups = mentorData.mentee_checkups || {};

            checkupEvents = Object.entries(menteeCheckups).map(([id, checkup]) => {
              const status = getCheckupStatus(checkup.checkDate, checkup.completed);
              return {
                id,
                title: `${formatCheckType(checkup.checkType)} - ${checkup.menteeName}`,
                start: new Date(checkup.checkDate),
                allDay: true,
                className: `mntr_cldr_checkup-${status}`,
                extendedProps: {
                  type: 'checkup',
                  checkType: checkup.checkType,
                  description: checkup.description,
                  menteeName: checkup.menteeName,
                  menteeId: checkup.menteeId,
                  step: checkup.step,
                  completed: checkup.completed,
                  status,
                  eventType: 'checkup',
                  menteePhoto: checkup.menteePhoto || '/icons/pfp.png'
                }
              };
            });
          }
        }

        // # Combine all events
        setEvents([...meetingEvents, ...checkupEvents]);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [user?.uid]);

  // # Format check type for display
  const formatCheckType = (type) => {
    switch(type) {
      case 'study_process': return 'Study Process';
      case 'practice_process': return 'Practice Process';
      case 'task_completion': return 'Task Completion';
      case 'practice_completion': return 'Practice Completion';
      case 'other': return 'Other';
      default: return type;
    }
  };

  // # Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const props = event.extendedProps;

    if (props.eventType === 'meeting') {
      setSelectedEvent({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        type: props.type,
        description: props.description,
        hostName: hostData[props.hostId]?.displayName || 'Unknown Host',
        isHost: props.isHost,
        meetingUrl: props.meetingUrl,
        status: props.status,
        invited: props.invited,
        eventType: 'meeting'
      });
    } else {
      setSelectedEvent({
        id: event.id,
        title: event.title,
        start: event.start,
        type: props.checkType,
        description: props.description,
        menteeName: props.menteeName,
        step: props.step,
        completed: props.completed,
        status: props.status,
        eventType: 'checkup',
        menteePhoto: props.menteePhoto
      });
    }
  };

  // # Close event details modal
  const handleCloseModal = () => {
    setSelectedEvent(null);
    setShowParticipants(false);
  };

  // # Toggle participants popup
  const toggleParticipantsPopup = () => {
    setShowParticipants(!showParticipants);
    setSearchQuery('');
  };

  // # Get time status label
  const getStatusLabel = (status) => {
    const now = new Date();
    const meetingIsPast = selectedEvent && new Date(selectedEvent.start) < now;
    
    switch (status) {
      case 'joinable':
        return meetingIsPast ? 'Join Active (6hr Window)' : 'Join Now';
      case 'upcoming-soon':
        return 'Coming Soon';
      case 'upcoming-week':
        return 'This Week';
      case 'past':
        return 'Past Meeting';
      default:
        return 'Scheduled';
    }
  };
  
  // # Format the date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Africa/Cairo'
    });
  };
  
  // # Format the time for display
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Cairo'
    });
  };

  // # Add a navigation handler
  const navigateToAgenda = async (e) => {
    e.preventDefault();
    // # Check if user is admin or tutor to determine navigation path
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const userRole = userDoc.exists() ? userDoc.data().role : null;
    
    if (userRole === 'tutor') {
      navigate('/tutor/mentorship/study?tab=agenda');
    } else {
      navigate('/admin/mentorship/study?tab=agenda');
    }
  };

  // # Participants Popup Component
  const ParticipantsPopup = () => {
    if (!selectedEvent || !showParticipants) return null;
    
    // # Filter participants based on search query
    const filteredParticipants = selectedEvent.invited.filter(uid => {
      const participant = participantsData[uid] || {};
      const searchLower = searchQuery.toLowerCase();
      return (
        participant.displayName?.toLowerCase().includes(searchLower) ||
        participant.email?.toLowerCase().includes(searchLower) ||
        participant.username?.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div className="mntr_cldr_participants_popup_overlay" onClick={toggleParticipantsPopup}>
        <div className="mntr_cldr_participants_popup" onClick={(e) => e.stopPropagation()}>
          <div className="mntr_cldr_participants_popup_header">
            <h3 className="mntr_cldr_participants_popup_title">
              <FaUsers /> Meeting Participants ({selectedEvent.invited.length})
            </h3>
            <button className="mntr_cldr_participants_popup_close" onClick={toggleParticipantsPopup}>
              <FaTimes />
            </button>
          </div>
          
          <div className="mntr_cldr_participants_search">
            <input
              type="text"
              placeholder="Search participants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mntr_cldr_participants_search_input"
            />
          </div>
          
          <div className="mntr_cldr_participants_list">
            {filteredParticipants.length > 0 ? (
              filteredParticipants.map((uid) => {
                const participant = participantsData[uid] || {};
                const isHost = uid === hostData[selectedEvent.hostId]?.uid;
                
                return (
                  <div key={uid} className="mntr_cldr_participant_item">
                    {participant.avatar ? (
                      <img src={participant.avatar} alt={participant.displayName} className="mntr_cldr_participant_avatar" />
                    ) : (
                      <div className="mntr_cldr_participant_avatar_placeholder">
                        {participant.displayName?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="mntr_cldr_participant_details">
                      <div className="mntr_cldr_participant_name_container">
                        <span className="mntr_cldr_participant_name">
                          {participant.displayName}
                          {participant.username && (
                            <span className="mntr_cldr_participant_username">@{participant.username}</span>
                          )}
                        </span>
                        {isHost && (
                          <span className="mntr_cldr_host_badge">
                            Host
                          </span>
                        )}
                      </div>
                      <span className="mntr_cldr_participant_email">{participant.email}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="mntr_cldr_participants_empty">
                <p>No participants match your search</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`mntr_cldr_mentor-container ${isDarkMode ? 'mntr_cldr_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mntr_cldr_mentor-main-content">
        <div className="mntr_cldr_title-container">
          <div className="mntr_cldr_title-box">
            <h2>Mentorship Calendar</h2>
            <div className="mntr_cldr_title-underline"></div>
          </div>
        </div>
        
        <div className="mntr_cldr_calendar-container">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            initialView="dayGridMonth"
            editable={false}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            events={events}
            eventClick={handleEventClick}
            height="auto"
            eventContent={renderEventContent}
          />
        </div>

        {/* # Event Details Modal */}
        {selectedEvent && (
          <div className="mntr_cldr_modal-overlay" onClick={handleCloseModal}>
            <div className="mntr_cldr_event-details" onClick={(e) => e.stopPropagation()}>
              {selectedEvent.eventType === 'meeting' ? (
                // # Existing meeting details UI
                <>
                  <div className={`mntr_cldr_event-status-banner mntr_cldr_status-${selectedEvent.status}`}>
                    {getStatusLabel(selectedEvent.status)}
                  </div>
                  
                  <div className="mntr_cldr_event-header">
                    <div className="mntr_cldr_event-type-container">
                      <FaVideo className="mntr_cldr_event-icon" />
                      <div className="mntr_cldr_event-type">
                        {selectedEvent.type.replace('_meetings', '').toUpperCase()}
                      </div>
                    </div>
                    
                    <h1 className="mntr_cldr_event-title">{selectedEvent.title}</h1>
                  </div>
                  
                  <div className="mntr_cldr_event-info-grid">
                    <div className="mntr_cldr_info-item">
                      <div className="mntr_cldr_info-label">
                        <FaUser className="mntr_cldr_icon" />
                        Host
                      </div>
                      <div className="mntr_cldr_info-value">
                        {selectedEvent.hostName}
                        {selectedEvent.isHost && <span className="mntr_cldr_host-badge">(You)</span>}
                      </div>
                    </div>
                    
                    <div className="mntr_cldr_info-item">
                      <div className="mntr_cldr_info-label">
                        <FaCalendarAlt className="mntr_cldr_icon" />
                        Date
                      </div>
                      <div className="mntr_cldr_info-value">
                        {formatDate(selectedEvent.start)}
                      </div>
                    </div>
                    
                    <div className="mntr_cldr_info-item">
                      <div className="mntr_cldr_info-label">
                        <FaClock className="mntr_cldr_icon" />
                        Time
                      </div>
                      <div className="mntr_cldr_info-value">
                        {formatTime(selectedEvent.start)}
                      </div>
                    </div>
                    
                    <div className="mntr_cldr_info-item mntr_cldr_full-width">
                      <div className="mntr_cldr_info-label">
                        <FaInfoCircle className="mntr_cldr_icon" />
                        Description
                      </div>
                      <div className="mntr_cldr_info-value mntr_cldr_event-description">
                        {selectedEvent.description || "No description provided."}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mntr_cldr_event-actions">
                    <a 
                      href={selectedEvent.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mntr_cldr_join-button ${selectedEvent.status === 'joinable' ? 'active' : ''} ${selectedEvent.status === 'upcoming-soon' ? 'upcoming' : ''}`}
                      onClick={(e) => selectedEvent.status !== 'joinable' && selectedEvent.status !== 'upcoming-soon' && e.preventDefault()}
                    >
                      Join Meeting
                    </a>
                    <button 
                      className="mntr_cldr_participants-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleParticipantsPopup();
                      }}
                    >
                      <FaUsers className="mntr_cldr_icon" />
                      Participants ({selectedEvent.invited.length})
                    </button>
                    <button 
                      className="mntr_cldr_close-button"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                // # Checkup details UI
                <>
                  <div className={`mntr_cldr_event-status-banner mntr_cldr_checkup-status-${selectedEvent.status}`}>
                    {selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
                  </div>
                  
                  <div className="mntr_cldr_event-header">
                    <div className="mntr_cldr_event-type-container">
                      <div className="mntr_cldr_checkup-modal-photo">
                        <img 
                          src={selectedEvent.menteePhoto || '/icons/pfp.png'} 
                          alt={selectedEvent.menteeName} 
                        />
                      </div>
                      <div className="mntr_cldr_event-type">
                        {formatCheckType(selectedEvent.type)}
                      </div>
                    </div>
                    
                    <h1 className="mntr_cldr_event-title">{selectedEvent.title}</h1>
                  </div>
                  
                  <div className="mntr_cldr_event-info-grid">
                    <div className="mntr_cldr_info-item">
                      <div className="mntr_cldr_info-label">
                        <FaUser className="mntr_cldr_icon" />
                        Mentee
                      </div>
                      <div className="mntr_cldr_info-value">
                        {selectedEvent.menteeName}
                        <span className="mntr_cldr_mentee-step-badge">
                          {selectedEvent.step}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mntr_cldr_info-item">
                      <div className="mntr_cldr_info-label">
                        <FaCalendarAlt className="mntr_cldr_icon" />
                        Due Date
                      </div>
                      <div className="mntr_cldr_info-value">
                        {formatDate(selectedEvent.start)}
                      </div>
                    </div>
                    
                    <div className="mntr_cldr_info-item mntr_cldr_full-width">
                      <div className="mntr_cldr_info-label">
                        <FaInfoCircle className="mntr_cldr_icon" />
                        Description
                      </div>
                      <div className="mntr_cldr_info-value mntr_cldr_event-description">
                        {selectedEvent.description || "No description provided."}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mntr_cldr_event-actions">
                    <button 
                      className="mntr_cldr_close-button"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                    <button 
                      className="mntr_cldr_agenda-button"
                      onClick={navigateToAgenda}
                    >
                      <FaClipboardCheck className="mntr_cldr_icon" />
                      Go to Agenda
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* # Participants Popup */}
        <ParticipantsPopup />
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

// # Custom event rendering function
const renderEventContent = (eventInfo) => {
  const props = eventInfo.event.extendedProps;
  
  if (props.eventType === 'meeting') {
    const status = props.status;
    const statusIcon = status === 'joinable' ? '➤' : 
                     status === 'upcoming-soon' ? '⏱' : 
                     status === 'past' ? '✓' : '•';
    
    // # Check if this is a past meeting that's still joinable (within 6 hour window)
    const isPastButJoinable = eventInfo.event.start < new Date() && status === 'joinable';
    
    // # Format time with shorter format for better fitting
    const timeStr = eventInfo.event.start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Africa/Cairo'
    });
    
    return (
      <div className={`mntr_cldr_calendar-event mntr_cldr_event-${status}`}>
        <div className="mntr_cldr_event-icon-wrapper">
          <span className="mntr_cldr_status-icon">{statusIcon}</span>
        </div>
        <div className="mntr_cldr_event-content">
          <h1 className="mntr_cldr_event-title">{eventInfo.event.title}</h1>
          <h2 className="mntr_cldr_event-host">
            {props.isHost ? 'You are hosting' : 'You are invited'}
            {isPastButJoinable && <span className="mntr_cldr_event-past-joinable"> (Active)</span>}
          </h2>
          <h3 className="mntr_cldr_event-time">{timeStr}</h3>
        </div>
      </div>
    );
  } else {
    const status = props.status;
    const statusIcon = status === 'completed' ? '✓' : 
                     status === 'overdue' ? '!' :
                     status === 'due-soon' ? '⏰' : '•';
    
    return (
      <div className={`mntr_cldr_calendar-checkup mntr_cldr_checkup-${status}`}>
        <div className="mntr_cldr_checkup-icon-wrapper">
          <img 
            src={props.menteePhoto || '/icons/pfp.png'} 
            alt={props.menteeName} 
            className="mntr_cldr_checkup-mentee-photo"
          />
        </div>
        <div className="mntr_cldr_checkup-content">
          <h1 className="mntr_cldr_checkup-title">
            {eventInfo.event.title}
            <span className="mntr_cldr_checkup-step-badge">
              {props.step}
            </span>
          </h1>
        </div>
      </div>
    );
  }
};

export default MentorCalendar; 